import { useCurrentAccountId } from '@zapier/identity';
import { useEffect, useState } from 'react';

import type { UseQuery } from '../types';
import type { Result } from './alertBannerNotificationsQuery';
import { alertBannerNotificationsQuery } from './alertBannerNotificationsQuery';

import * as AnnualBillingUpsell from '../banners/AnnualBillingUpsell';
import * as BannerMessage from '../banners/BannerMessage';
import * as CompaniesTrialEnded from '../banners/CompaniesTrialEnded';
import * as CompaniesTrialing from '../banners/CompaniesTrialing';
import * as ProfessionalTrialEnded from '../banners/ProfessionalTrialEnded';
import * as ProfessionalTrialing from '../banners/ProfessionalTrialing';
import * as RefreshRequired from '../banners/RefreshRequired';
import * as TeamsDowngraded from '../banners/TeamsDowngraded';
import * as TeamsTrialEnded from '../banners/TeamsTrialEnded';
import * as OverdueInvoice from '../banners/OverdueInvoice';
import * as TeamsTrialing from '../banners/TeamsTrialing';
import { BannerProps } from '../banners/types';
import * as UnlimitedAccount from '../banners/UnlimitedAccount';

type UseAlertOptions = {
  useQuery: UseQuery;
};

type UseAlertReturn = {
  isLoading: boolean;
  data: Result['data'];
  Component?: (props: BannerProps) => JSX.Element | null;
};

const possibleComponents = [
  RefreshRequired,
  BannerMessage,
  UnlimitedAccount,
  CompaniesTrialing,
  CompaniesTrialEnded,
  TeamsTrialing,
  TeamsTrialEnded,
  OverdueInvoice,
  TeamsDowngraded,
  ProfessionalTrialing,
  ProfessionalTrialEnded,
  AnnualBillingUpsell,
];

export const useAlertBanner = (opts: UseAlertOptions): UseAlertReturn => {
  const currentAccountId = useCurrentAccountId();
  const [lastRenderedTime, setLastRenderedTime] = useState(Date.now());
  const [isExcludedUrl, setIsExcludedUrl] = useState(false);

  // Rerender every few minutes to ensure data is up to date. This is
  // especially important for `RefreshRequired`.
  useEffect(() => {
    setTimeout(() => {
      setLastRenderedTime(Date.now());
    }, 5 * 60 * 60 * 1000);
  }, [lastRenderedTime]);

  useEffect(() => {
    setIsExcludedUrl(window.location.pathname.includes('team-account-setup'));
  }, []);

  const result: Result = opts.useQuery(alertBannerNotificationsQuery, {
    ssr: false,
    variables: {
      accountId: currentAccountId,
    },
  });
  const { data } = result;
  const Component =
    result.loading ||
    result.error ||
    !data ||
    !data.currentUser ||
    !data.currentAccount
      ? undefined
      : possibleComponents.find((component) => {
          return component.shouldRender(data, isExcludedUrl);
        })?.Component;
  return {
    data,
    Component,
    isLoading: result.loading,
  };
};
